import React, { useState, useMemo } from 'react';
import {
  Search, X, Eye, ChevronRight, FileText, Package,
  DollarSign, Receipt, CreditCard, ClipboardCheck, Filter,
  CheckSquare, Square, XCircle, ArrowRight, AlertCircle, Calendar
} from 'lucide-react';
import { DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS } from '../constants/status';

// 单据类型配置
const DOC_TYPES = {
  arrival_notice: {
    key: 'arrival_notice',
    label: '预到货通知单',
    icon: FileText,
    color: 'blue',
    canSelect: false,
    nextType: null,
    nextLabel: null,
    statuses: ['预约中', '已送达'],
  },
  receipt: {
    key: 'receipt',
    label: '收货单',
    icon: ClipboardCheck,
    color: 'green',
    canSelect: false,
    nextType: null,
    nextLabel: null,
    statuses: ['待确认', '已确认', '已完成'],
  },
  stockin: {
    key: 'stockin',
    label: '入库单',
    icon: Package,
    color: 'purple',
    canSelect: true,
    nextType: 'settlement',
    nextLabel: '结算单',
    selectableStatus: '未发起',  // 只有"未发起"的入库单可发起结算
    statuses: ['未发起', '已发起'],
  },
  settlement: {
    key: 'settlement',
    label: '结算单',
    icon: DollarSign,
    color: 'orange',
    canSelect: true,
    // 结算单有两种下游操作，根据开票状态决定
    getNextAction: (doc) => {
      if (doc.status === '已确认' && doc.invoiceStatus === '未开票') {
        return { nextType: 'invoice', nextLabel: '发票单' };
      }
      if (doc.status === '已确认' && doc.invoiceStatus === '已开票') {
        return { nextType: 'payment', nextLabel: '付款单' };
      }
      return null;
    },
    // 已确认 && 未开票 可发起开票，已确认 && 已开票 可发起付款
    isSelectable: (doc) => doc.status === '已确认',
    statuses: ['待确认', '已确认'],
  },
  invoice: {
    key: 'invoice',
    label: '发票单',
    icon: Receipt,
    color: 'teal',
    canSelect: true,
    nextType: 'payment',
    nextLabel: '付款单',
    selectableStatus: '已开票',  // 只有"已开票"的发票单可发起付款申请
    statuses: ['待开票', '审批中', '已开票'],
  },
  payment: {
    key: 'payment',
    label: '付款单',
    icon: CreditCard,
    color: 'green',
    canSelect: false,
    nextType: null,
    nextLabel: null,
    statuses: ['审批中', '待付款', '已付款'],
  },
};

// 从送货单数据提取各类型单据
const extractDocuments = (deliveries, orders) => {
  const documents = {
    arrival_notice: [],
    receipt: [],
    stockin: [],
    settlement: [],
    invoice: [],
    payment: [],
  };

  deliveries.forEach(delivery => {
    // 预到货通知单（有预约信息即有）
    // 状态：预约中、已送达
    if (delivery.reservation) {
      documents.arrival_notice.push({
        id: delivery.reservation.reservationNo,
        docNo: delivery.reservation.reservationNo,
        docType: 'arrival_notice',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        materialName: delivery.materialName,
        vehicleNo: delivery.reservation.vehicleNo,
        driverName: delivery.reservation.driverName,
        driverPhone: delivery.reservation.driverPhone,
        reservationTime: delivery.reservation.time,
        status: delivery.arrival ? '已送达' : '预约中',
        createdAt: delivery.createdAt,
        delivery,  // 添加完整的 delivery 对象
      });
    }

    // 收货单（有到场信息）
    // 状态：待确认、已确认、已完成
    if (delivery.arrival) {
      let receiptStatus = '待确认';
      if (delivery.storage) {
        receiptStatus = '已完成';  // 已入库则已完成
      } else if (delivery.arrival.confirmed) {
        receiptStatus = '已确认';
      }

      documents.receipt.push({
        id: `RC-${delivery.id}`,
        docNo: delivery.deliveryNo.replace('DEL', 'RC'),
        docType: 'receipt',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        materialName: delivery.materialName,
        vehicleNo: delivery.reservation?.vehicleNo,
        arrivalTime: delivery.arrival.at,
        arrivalOperator: delivery.arrival.operator,
        arrivalPhoto: delivery.arrival.photo,
        status: receiptStatus,
        createdAt: delivery.arrival.at,
        delivery,  // 添加完整的 delivery 对象
      });
    }

    // 入库单
    // 状态：未发起、已发起（基于是否有结算单）
    if (delivery.storage) {
      // 入库单结算状态：有结算单则为"已发起"，否则为"未发起"
      const stockinStatus = delivery.settlement ? '已发起' : '未发起';

      // 计算含税金额和不含税金额
      const price = delivery.price || orders.find(o => o.id === delivery.orderId)?.price || 0;
      const taxRate = delivery.storage.taxRate || 13;
      const amountWithoutTax = delivery.storage.actualWeight * price;
      const amountWithTax = amountWithoutTax * (1 + taxRate / 100);

      documents.stockin.push({
        id: delivery.storage.stockInNo,
        docNo: delivery.storage.stockInNo,
        docType: 'stockin',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        materialName: delivery.materialName,
        vehicleNo: delivery.reservation?.vehicleNo,
        grossWeight: delivery.storage.grossWeight,
        tareWeight: delivery.storage.tareWeight,
        netWeight: delivery.storage.netWeight,
        deduction: delivery.storage.deduction,
        actualWeight: delivery.storage.actualWeight,
        quality: delivery.storage.quality,
        stockInTime: delivery.storage.at,
        operator: delivery.storage.operator,
        status: stockinStatus,
        // 新增字段
        amountWithTax: delivery.storage.amountWithTax || amountWithTax,
        amountWithoutTax: delivery.storage.amountWithoutTax || amountWithoutTax,
        taxRate: taxRate,
        invoiceType: delivery.storage.invoiceType || '增值税专用发票',
        price: price,
        // 5个环节信息
        entryInfo: delivery.storage.entryInfo || null,
        entryWeighInfo: delivery.storage.entryWeighInfo || null,
        qualityInfo: delivery.storage.qualityInfo || null,
        exitWeighInfo: delivery.storage.exitWeighInfo || null,
        exitCheckInfo: delivery.storage.exitCheckInfo || null,
        photos: delivery.storage.photos || null,
        createdAt: delivery.storage.at,
        delivery,
      });
    }

    // 结算单
    // 结算状态：待确认、已确认
    // 开票状态：已开票、未开票
    if (delivery.settlement) {
      const settlementStatus = delivery.settlement.confirmedBySupplier ? '已确认' : '待确认';
      const invoiceStatus = delivery.invoice ? '已开票' : '未开票';

      documents.settlement.push({
        id: delivery.settlement.settlementNo,
        docNo: delivery.settlement.settlementNo,
        docType: 'settlement',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        materialName: delivery.materialName,
        actualWeight: delivery.settlement.actualWeight || delivery.storage?.actualWeight,
        unitPrice: delivery.settlement.unitPrice,
        amount: delivery.settlement.amount,
        confirmedBySupplier: delivery.settlement.confirmedBySupplier,
        confirmedAt: delivery.settlement.confirmedAt,
        status: settlementStatus,
        invoiceStatus: invoiceStatus,  // 新增开票状态
        canInvoice: settlementStatus === '已结算',  // 可发起开票：已结算
        canPayment: settlementStatus === '已结算' && invoiceStatus === '已开票',  // 可发起付款：已结算且已开票
        createdAt: delivery.storage?.at || delivery.createdAt,
        delivery,
        // 合并单据相关：关联的入库单数量 >= 2 才算合并单据
        isMerged: (delivery.settlement.sourceStockInNos?.length || 0) >= 2,
        sourceStockInNos: delivery.settlement.sourceStockInNos || null,
        sourceDeliveryIds: delivery.settlement.sourceDeliveryIds || null,
      });
    }

    // 发票单
    // 状态：待开票、审批中、已开票
    if (delivery.invoice) {
      let invoiceStatus = '已开票';
      // 根据付款状态判断
      if (delivery.payment) {
        invoiceStatus = '已开票';
      } else if (delivery.invoice.pendingApproval) {
        invoiceStatus = '审批中';
      }

      documents.invoice.push({
        id: delivery.invoice.no,
        docNo: delivery.invoice.no,
        docType: 'invoice',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        invoiceType: delivery.invoice.type,
        amount: delivery.invoice.amount,
        invoiceDate: delivery.invoice.at,
        invoiceFile: delivery.invoice.file,
        status: invoiceStatus,
        createdAt: delivery.invoice.at,
        delivery,
        // 合并单据相关：关联的结算单数量 >= 2 才算合并单据
        isMerged: (delivery.invoice.sourceSettlementNos?.length || 0) >= 2,
        sourceSettlementNos: delivery.invoice.sourceSettlementNos || null,
      });
    }

    // 付款单
    // 状态：审批中、待付款、已付款
    if (delivery.payment) {
      let paymentStatus = '已付款';
      if (delivery.payment.pendingApproval) {
        paymentStatus = '审批中';
      } else if (delivery.payment.pendingPay) {
        paymentStatus = '待付款';
      }

      documents.payment.push({
        id: delivery.payment.no,
        docNo: delivery.payment.no,
        docType: 'payment',
        orderId: delivery.orderId,
        orderNo: delivery.orderNo,
        deliveryId: delivery.id,
        deliveryNo: delivery.deliveryNo,
        supplierId: delivery.supplierId,
        supplierName: delivery.supplierName,
        baseId: delivery.baseId,
        baseName: delivery.baseName,
        amount: delivery.payment.amount,
        paymentTime: delivery.payment.at,
        paymentMethod: delivery.payment.method,
        transactionNo: delivery.payment.transactionNo,
        status: paymentStatus,
        createdAt: delivery.payment.at,
        delivery,
        // 合并单据相关：关联的发票单数量 >= 2 才算合并单据
        isMerged: (delivery.payment.sourceInvoiceNos?.length || 0) >= 2,
        sourceInvoiceNos: delivery.payment.sourceInvoiceNos || null,
        sourceSettlementNos: delivery.payment.sourceSettlementNos || null,
      });
    }
  });

  return documents;
};

// 状态徽章
const StatusBadge = ({ status, type }) => {
  const colorMap = {
    // 预到货通知单
    '预约中': 'bg-blue-100 text-blue-700',
    '已送达': 'bg-green-100 text-green-700',
    // 收货单
    '待确认': 'bg-amber-100 text-amber-700',
    '已确认': 'bg-blue-100 text-blue-700',
    '已完成': 'bg-green-100 text-green-700',
    // 入库单 - 结算状态
    '未发起': 'bg-slate-100 text-slate-700',
    '已发起': 'bg-green-100 text-green-700',
    // 开票状态
    '已开票': 'bg-teal-100 text-teal-700',
    '未开票': 'bg-slate-100 text-slate-700',
    // 发票单
    '待开票': 'bg-amber-100 text-amber-700',
    // 付款单
    '待付款': 'bg-amber-100 text-amber-700',
    '已付款': 'bg-green-100 text-green-700',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// 送货单状态徽章
const DeliveryStatusBadge = ({ status }) => {
  const statusConfig = DELIVERY_STATUS_COLORS[status] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  const statusLabel = DELIVERY_STATUS_LABELS[status] || status;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
      {statusLabel}
    </span>
  );
};

// 批量结算弹窗 - 按供应商分组显示未发起结算的入库单
const BatchSettlementModal = ({ isOpen, onClose, onConfirm, stockins, onToggleSelect, selectedIds }) => {
  // 按供应商分组 - hooks must be called before early return
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    stockins.forEach(doc => {
      if (!groups[doc.supplierName]) {
        groups[doc.supplierName] = [];
      }
      groups[doc.supplierName].push(doc);
    });
    return groups;
  }, [stockins]);

  const selectedDocs = stockins.filter(doc => selectedIds.includes(doc.id));
  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0);
  const totalWeight = selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">批量结算</h3>
            <p className="text-sm text-slate-500 mt-1">按供应商分组显示未发起结算的入库单</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedBySupplier).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package size={48} className="mx-auto text-slate-300 mb-4" />
              <p>暂无未发起结算的入库单</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedBySupplier).map(([supplier, docs]) => {
                const supplierSelectedIds = docs.filter(d => selectedIds.includes(d.id)).map(d => d.id);
                const isAllSelected = supplierSelectedIds.length === docs.length;
                const supplierTotal = docs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0);

                return (
                  <div key={supplier} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* 供应商标题栏 */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            // 切换该供应商下所有入库单的选中状态
                            docs.forEach(doc => {
                              onToggleSelect(doc.id, !isAllSelected);
                            });
                          }}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {isAllSelected ? (
                            <CheckSquare size={18} className="text-blue-500" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                        <span className="font-medium text-slate-800">{supplier}</span>
                        <span className="text-xs text-slate-500">{docs.length} 条入库单</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">小计：</span>
                        <span className="font-bold text-red-600">¥{supplierTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* 入库单列表 */}
                    <div className="divide-y divide-slate-100">
                      {docs.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            onClick={() => onToggleSelect(doc.id, !isSelected)}
                            className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${
                              isSelected ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSelect(doc.id, !isSelected);
                                  }}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  {isSelected ? (
                                    <CheckSquare size={18} className="text-blue-500" />
                                  ) : (
                                    <Square size={18} />
                                  )}
                                </button>
                                <div>
                                  <div className="text-sm font-medium text-slate-800">{doc.docNo}</div>
                                  <div className="text-xs text-slate-500 mt-0.5">
                                    {doc.stockInTime} · {doc.vehicleNo} · {doc.actualWeight}吨
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-red-600">¥{(doc.amountWithTax || 0).toLocaleString()}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  不含税：¥{(doc.amountWithoutTax || 0).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
          )}
        </div>

        {/* 底部汇总和操作 */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-sm">
                <span className="text-slate-500">已选择：</span>
                <span className="font-bold text-blue-600 ml-1">{selectedDocs.length}</span>
                <span className="text-slate-500 ml-1">条入库单</span>
              </div>
              <div className="text-sm">
                <span className="text-slate-500">总重量：</span>
                <span className="font-bold text-slate-800 ml-1">{totalWeight.toFixed(2)} 吨</span>
              </div>
              <div className="text-sm">
                <span className="text-slate-500">总金额：</span>
                <span className="font-bold text-red-600 ml-1">¥{totalAmount.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                取消
              </button>
              <button
                onClick={() => onConfirm(selectedDocs)}
                disabled={selectedDocs.length === 0}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedDocs.length > 0
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                确认生成结算单
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 批量开票弹窗 - 按供应商分组显示未开票的结算单
const BatchInvoiceModal = ({ isOpen, onClose, onConfirm, settlements, onToggleSelect, selectedIds }) => {
  // 按供应商分组 - hooks must be called before early return
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    settlements.forEach(doc => {
      if (!groups[doc.supplierName]) {
        groups[doc.supplierName] = [];
      }
      groups[doc.supplierName].push(doc);
    });
    return groups;
  }, [settlements]);

  const selectedDocs = settlements.filter(doc => selectedIds.includes(doc.id));
  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0);
  const totalWeight = selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">批量开票</h3>
            <p className="text-sm text-slate-500 mt-1">按供应商分组显示待开票的结算单</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedBySupplier).length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
              <p>暂无待开票的结算单</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedBySupplier).map(([supplier, docs]) => {
                const supplierSelectedIds = docs.filter(d => selectedIds.includes(d.id)).map(d => d.id);
                const isAllSelected = supplierSelectedIds.length === docs.length;
                const supplierTotal = docs.reduce((sum, doc) => sum + (doc.amount || 0), 0);

                return (
                  <div key={supplier} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* 供应商标题栏 */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            // 切换该供应商下所有结算单的选中状态
                            docs.forEach(doc => {
                              onToggleSelect(doc.id, !isAllSelected);
                            });
                          }}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          {isAllSelected ? (
                            <CheckSquare size={18} className="text-blue-500" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                        <span className="font-medium text-slate-800">{supplier}</span>
                        <span className="text-xs text-slate-500">{docs.length} 条结算单</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">小计：</span>
                        <span className="font-bold text-red-600">¥{supplierTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* 结算单列表 */}
                    <div className="divide-y divide-slate-100">
                      {docs.map(doc => {
                        const isSelected = selectedIds.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            onClick={() => onToggleSelect(doc.id, !isSelected)}
                            className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${
                              isSelected ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleSelect(doc.id, !isSelected);
                                }}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                {isSelected ? (
                                  <CheckSquare size={18} className="text-blue-500" />
                                ) : (
                                  <Square size={18} />
                                )}
                              </button>
                              <div>
                                <div className="text-sm font-medium text-slate-800">{doc.docNo}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  实重: {doc.actualWeight}吨 · 单价: ¥{doc.unitPrice}/吨
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-red-600">¥{(doc.amount || 0).toLocaleString()}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {doc.confirmedAt || '待确认'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 底部汇总和操作 */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-sm">
                <span className="text-slate-500">已选择：</span>
                <span className="font-bold text-blue-600 ml-1">{selectedDocs.length}</span>
                <span className="text-slate-500 ml-1">条结算单</span>
              </div>
              <div className="text-sm">
                <span className="text-slate-500">总重量：</span>
                <span className="font-bold text-slate-800 ml-1">{totalWeight.toFixed(2)} 吨</span>
              </div>
              <div className="text-sm">
                <span className="text-slate-500">总金额：</span>
                <span className="font-bold text-red-600 ml-1">¥{totalAmount.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                取消
              </button>
              <button
                onClick={() => onConfirm(selectedDocs)}
                disabled={selectedDocs.length === 0}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedDocs.length > 0
                    ? 'bg-teal-500 text-white hover:bg-teal-600'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                确认生成发票单
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 生成单据弹窗
const GenerateModal = ({ isOpen, onClose, onConfirm, selectedDocs, targetType, targetLabel }) => {
  if (!isOpen) return null;

  const totalAmount = selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0);
  const totalWeight = selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0);
  const suppliers = [...new Set(selectedDocs.map(d => d.supplierName))];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-800">生成{targetLabel}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {/* 警告：多个供应商 */}
          {suppliers.length > 1 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-700">
                <p className="font-medium">注意：选中的单据包含多个供应商</p>
                <p className="mt-1">建议同一供应商的单据合并生成，当前包含：{suppliers.join('、')}</p>
              </div>
            </div>
          )}

          {/* 汇总信息 */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-slate-500 mb-3">已选择 {selectedDocs.length} 条单据</p>
            <div className="grid grid-cols-2 gap-4">
              {totalWeight > 0 && (
                <div>
                  <p className="text-xs text-slate-500">总重量</p>
                  <p className="text-lg font-bold text-slate-800">{totalWeight.toFixed(2)} 吨</p>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-500">总金额</p>
                <p className="text-lg font-bold text-red-600">¥{totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* 单据列表 */}
          <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
            {selectedDocs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-b-0">
                <div>
                  <span className="text-sm font-medium text-slate-800">{doc.docNo}</span>
                  <span className="text-xs text-slate-500 ml-2">{doc.supplierName}</span>
                </div>
                <span className="text-sm font-medium text-slate-600">¥{(doc.amount || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
          >
            确认生成{targetLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// 主组件
export default function DocumentView({ deliveries, orders, onDocumentClick, onGenerateDocument, onOrderClick, onDeliveryClick }) {
  const [activeDocType, setActiveDocType] = useState('arrival_notice');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBase, setFilterBase] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');  // 新增状态筛选
  const [filterDateStart, setFilterDateStart] = useState('');  // 预约时间开始
  const [filterDateEnd, setFilterDateEnd] = useState('');  // 预约时间结束
  const [selectedIds, setSelectedIds] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showBatchSettlementModal, setShowBatchSettlementModal] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState([]);
  const [showBatchInvoiceModal, setShowBatchInvoiceModal] = useState(false);
  const [batchInvoiceSelectedIds, setBatchInvoiceSelectedIds] = useState([]);

  // 提取所有单据
  const allDocuments = useMemo(() => {
    return extractDocuments(deliveries, orders);
  }, [deliveries, orders]);

  // 获取所有基地和供应商（用于筛选）
  const { bases, suppliers } = useMemo(() => {
    const baseSet = new Set();
    const supplierSet = new Set();

    deliveries.forEach(d => {
      if (d.baseName) baseSet.add(d.baseName);
      if (d.supplierName) supplierSet.add(d.supplierName);
    });

    return {
      bases: Array.from(baseSet).sort(),
      suppliers: Array.from(supplierSet).sort(),
    };
  }, [deliveries]);

  // 当前选中类型的单据列表
  const currentDocs = useMemo(() => allDocuments[activeDocType] || [], [allDocuments, activeDocType]);
  const docConfig = DOC_TYPES[activeDocType];

  // 筛选后的单据
  const filteredDocs = useMemo(() => {
    let result = [...currentDocs];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc =>
        doc.docNo?.toLowerCase().includes(query) ||
        doc.orderNo?.toLowerCase().includes(query) ||
        doc.deliveryNo?.toLowerCase().includes(query) ||
        doc.supplierName?.toLowerCase().includes(query) ||
        doc.vehicleNo?.toLowerCase().includes(query)
      );
    }

    // 基地筛选
    if (filterBase) {
      result = result.filter(doc => doc.baseName === filterBase);
    }

    // 供应商筛选
    if (filterSupplier) {
      result = result.filter(doc => doc.supplierName === filterSupplier);
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(doc => doc.status === filterStatus);
    }

    // 预约时间筛选（预到货通知单和收货单）
    if (activeDocType === 'arrival_notice' || activeDocType === 'receipt' || activeDocType === 'stockin') {
      if (filterDateStart) {
        result = result.filter(doc => {
          // 预到货通知单使用 reservationTime，收货单使用 arrivalTime，入库单使用 stockInTime
          let timeField;
          if (activeDocType === 'arrival_notice') {
            timeField = doc.reservationTime;
          } else if (activeDocType === 'receipt') {
            timeField = doc.arrivalTime;
          } else {
            timeField = doc.stockInTime;
          }
          const docDate = timeField?.split(' ')[0]; // 提取日期部分
          return docDate && docDate >= filterDateStart;
        });
      }
      if (filterDateEnd) {
        result = result.filter(doc => {
          let timeField;
          if (activeDocType === 'arrival_notice') {
            timeField = doc.reservationTime;
          } else if (activeDocType === 'receipt') {
            timeField = doc.arrivalTime;
          } else {
            timeField = doc.stockInTime;
          }
          const docDate = timeField?.split(' ')[0]; // 提取日期部分
          return docDate && docDate <= filterDateEnd;
        });
      }
    }

    // 按时间倒序
    result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return result;
  }, [currentDocs, searchQuery, filterBase, filterSupplier, filterStatus, filterDateStart, filterDateEnd, activeDocType]);

  // 可选择状态的单据
  const selectableDocs = useMemo(() => {
    if (!docConfig.canSelect) return [];
    // 结算单使用自定义的 isSelectable 函数
    if (docConfig.isSelectable) {
      return filteredDocs.filter(doc => docConfig.isSelectable(doc));
    }
    // 其他类型使用 selectableStatus
    if (docConfig.selectableStatus) {
      return filteredDocs.filter(doc => doc.status === docConfig.selectableStatus);
    }
    return [];
  }, [filteredDocs, docConfig]);

  // 选中的单据
  const selectedDocs = useMemo(() => {
    return filteredDocs.filter(doc => selectedIds.includes(doc.id));
  }, [filteredDocs, selectedIds]);

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterBase('');
    setFilterSupplier('');
    setFilterStatus('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // 切换选择
  const toggleSelect = (docId) => {
    setSelectedIds(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedIds.length === selectableDocs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableDocs.map(doc => doc.id));
    }
  };

  // 清除选择
  const clearSelection = () => {
    setSelectedIds([]);
  };

  // 获取未发起结算的入库单（用于批量结算）
  const unsettledStockins = useMemo(() => {
    if (activeDocType !== 'stockin') return [];
    return filteredDocs.filter(doc => doc.status === '未发起');
  }, [activeDocType, filteredDocs]);

  // 批量结算 - 切换选择
  const toggleBatchSelect = (docId, isSelected) => {
    setBatchSelectedIds(prev =>
      isSelected
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };


  // 打开批量结算弹窗
  const openBatchSettlement = () => {
    // 自动选中所有未发起结算的入库单
    setBatchSelectedIds(unsettledStockins.map(doc => doc.id));
    setShowBatchSettlementModal(true);
  };

  // 确认批量生成结算单
  const confirmBatchSettlement = (selectedDocs) => {
    onGenerateDocument?.({
      sourceType: 'stockin',
      targetType: 'settlement',
      targetLabel: '结算单',
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amountWithTax || 0), 0),
      totalWeight: selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
      isBatch: true,
    });
    setShowBatchSettlementModal(false);
    setBatchSelectedIds([]);
  };

  // 获取待开票的结算单（用于批量开票）
  const pendingInvoiceSettlements = useMemo(() => {
    if (activeDocType !== 'settlement') return [];
    return filteredDocs.filter(doc => doc.status === '已确认' && doc.invoiceStatus === '未开票');
  }, [activeDocType, filteredDocs]);

  // 批量开票 - 切换选择
  const toggleBatchInvoiceSelect = (docId, isSelected) => {
    setBatchInvoiceSelectedIds(prev =>
      isSelected
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };

  // 打开批量开票弹窗
  const openBatchInvoice = () => {
    // 自动选中所有待开票的结算单
    setBatchInvoiceSelectedIds(pendingInvoiceSettlements.map(doc => doc.id));
    setShowBatchInvoiceModal(true);
  };

  // 确认批量生成发票单
  const confirmBatchInvoice = (selectedDocs) => {
    onGenerateDocument?.({
      sourceType: 'settlement',
      targetType: 'invoice',
      targetLabel: '发票单',
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0),
      totalWeight: selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
      isBatch: true,
    });
    setShowBatchInvoiceModal(false);
    setBatchInvoiceSelectedIds([]);
  };

  // 切换类型时清除选择和状态筛选
  const handleTypeChange = (type) => {
    setActiveDocType(type);
    setSelectedIds([]);
    setFilterStatus('');  // 切换类型时重置状态筛选
    setFilterDateStart('');  // 切换类型时重置日期筛选
    setFilterDateEnd('');
  };

  // 生成下游单据
  const handleGenerate = () => {
    setShowGenerateModal(true);
  };

  const confirmGenerate = () => {
    // 获取目标类型
    let targetType = docConfig.nextType;
    let targetLabel = docConfig.nextLabel;
    if (docConfig.getNextAction) {
      const action = docConfig.getNextAction(selectedDocs[0]);
      targetType = action?.nextType;
      targetLabel = action?.nextLabel;
    }

    onGenerateDocument?.({
      sourceType: activeDocType,
      targetType,
      targetLabel,
      sourceDocs: selectedDocs,
      totalAmount: selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0),
      totalWeight: selectedDocs.reduce((sum, doc) => sum + (doc.actualWeight || 0), 0),
      suppliers: [...new Set(selectedDocs.map(d => d.supplierName))],
    });
    setShowGenerateModal(false);
    setSelectedIds([]);
  };

  const hasFilters = searchQuery || filterBase || filterSupplier || filterStatus || filterDateStart || filterDateEnd;

  // 渲染表格列
  const renderTableColumns = (doc) => {
    switch (activeDocType) {
      case 'arrival_notice':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4">
              {onOrderClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(doc.orderId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.orderNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.orderNo}</span>
              )}
            </td>
            <td className="p-4">
              {onDeliveryClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeliveryClick(doc.deliveryId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.deliveryNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.deliveryNo}</span>
              )}
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.vehicleNo}</td>
            <td className="p-4 text-slate-600">{doc.reservationTime}</td>
            <td className="p-4 text-slate-600">{doc.baseName}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="arrival_notice" /></td>
            <td className="p-4"><DeliveryStatusBadge status={doc.delivery?.status} /></td>
          </>
        );

      case 'receipt':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4">
              {onOrderClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(doc.orderId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.orderNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.orderNo}</span>
              )}
            </td>
            <td className="p-4">
              {onDeliveryClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeliveryClick(doc.deliveryId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.deliveryNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.deliveryNo}</span>
              )}
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.vehicleNo}</td>
            <td className="p-4 text-slate-600">{doc.arrivalTime}</td>
            <td className="p-4 text-slate-600">{doc.baseName}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="receipt" /></td>
            <td className="p-4"><DeliveryStatusBadge status={doc.delivery?.status} /></td>
          </>
        );

      case 'stockin':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4">
              {onOrderClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderClick(doc.orderId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.orderNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.orderNo}</span>
              )}
            </td>
            <td className="p-4">
              {onDeliveryClick ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeliveryClick(doc.deliveryId);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  {doc.deliveryNo}
                </button>
              ) : (
                <span className="text-slate-800">{doc.deliveryNo}</span>
              )}
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.materialName}</td>
            <td className="p-4 text-slate-600">{doc.vehicleNo}</td>
            <td className="p-4 text-right text-slate-800">{doc.grossWeight}</td>
            <td className="p-4 text-right text-slate-800">{doc.tareWeight}</td>
            <td className="p-4 text-right font-medium text-purple-600">{doc.actualWeight}</td>
            <td className="p-4 text-right text-slate-800">{doc.deduction}</td>
            <td className="p-4 text-right text-red-600 font-medium">¥{doc.amountWithTax?.toLocaleString()}</td>
            <td className="p-4 text-right text-slate-800">¥{doc.amountWithoutTax?.toLocaleString()}</td>
            <td className="p-4 text-slate-600">{doc.taxRate}%</td>
            <td className="p-4 text-slate-600">{doc.invoiceType}</td>
            <td className="p-4 text-slate-600">{doc.stockInTime}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="stockin" /></td>
            <td className="p-4"><DeliveryStatusBadge status={doc.delivery?.status} /></td>
          </>
        );

      case 'settlement':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.actualWeight} 吨</td>
            <td className="p-4 text-slate-600">¥{doc.unitPrice}/吨</td>
            <td className="p-4 text-right font-bold text-orange-600">¥{doc.amount?.toLocaleString()}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="settlement" /></td>
            <td className="p-4"><StatusBadge status={doc.invoiceStatus} type="invoice_status" /></td>
          </>
        );

      case 'invoice':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.invoiceType}</td>
            <td className="p-4 text-right font-bold text-teal-600">¥{doc.amount?.toLocaleString()}</td>
            <td className="p-4 text-slate-600">{doc.invoiceDate}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="invoice" /></td>
          </>
        );

      case 'payment':
        return (
          <>
            <td className="p-4">
              <span className="font-mono text-xs text-slate-600">{doc.docNo}</span>
            </td>
            <td className="p-4 text-slate-800 font-medium">{doc.supplierName}</td>
            <td className="p-4 text-slate-600">{doc.paymentMethod}</td>
            <td className="p-4 text-right font-bold text-green-600">¥{doc.amount?.toLocaleString()}</td>
            <td className="p-4 text-slate-600">{doc.paymentTime}</td>
            <td className="p-4"><StatusBadge status={doc.status} type="payment" /></td>
          </>
        );

      default:
        return null;
    }
  };

  // 渲染表头
  const renderTableHeader = () => {
    const selectHeader = docConfig.canSelect ? (
      <th className="p-4 w-12">
        <button
          onClick={toggleSelectAll}
          className="text-slate-400 hover:text-slate-600"
        >
          {selectedIds.length === selectableDocs.length && selectableDocs.length > 0 ? (
            <CheckSquare size={18} className="text-blue-500" />
          ) : (
            <Square size={18} />
          )}
        </button>
      </th>
    ) : null;

    switch (activeDocType) {
      case 'arrival_notice':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">单据编号</th>
            <th className="p-4 font-medium text-slate-500">订单号</th>
            <th className="p-4 font-medium text-slate-500">送货单号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">车牌号</th>
            <th className="p-4 font-medium text-slate-500">预约时间</th>
            <th className="p-4 font-medium text-slate-500">基地</th>
            <th className="p-4 font-medium text-slate-500">状态</th>
            <th className="p-4 font-medium text-slate-500">送货单状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      case 'receipt':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">单据编号</th>
            <th className="p-4 font-medium text-slate-500">订单号</th>
            <th className="p-4 font-medium text-slate-500">送货单号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">车牌号</th>
            <th className="p-4 font-medium text-slate-500">到场时间</th>
            <th className="p-4 font-medium text-slate-500">基地</th>
            <th className="p-4 font-medium text-slate-500">状态</th>
            <th className="p-4 font-medium text-slate-500">送货单状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      case 'stockin':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">入库单号</th>
            <th className="p-4 font-medium text-slate-500">订单号</th>
            <th className="p-4 font-medium text-slate-500">送货单号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">物料</th>
            <th className="p-4 font-medium text-slate-500">车牌号</th>
            <th className="p-4 font-medium text-slate-500 text-right">毛重(吨)</th>
            <th className="p-4 font-medium text-slate-500 text-right">皮重(吨)</th>
            <th className="p-4 font-medium text-slate-500 text-right">收货量(吨)</th>
            <th className="p-4 font-medium text-slate-500 text-right">扣杂量(吨)</th>
            <th className="p-4 font-medium text-slate-500 text-right">含税金额</th>
            <th className="p-4 font-medium text-slate-500 text-right">不含税金额</th>
            <th className="p-4 font-medium text-slate-500">税率</th>
            <th className="p-4 font-medium text-slate-500">开票类型</th>
            <th className="p-4 font-medium text-slate-500">入库时间</th>
            <th className="p-4 font-medium text-slate-500">结算状态</th>
            <th className="p-4 font-medium text-slate-500">送货单状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      case 'settlement':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">结算单号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">实重</th>
            <th className="p-4 font-medium text-slate-500">单价</th>
            <th className="p-4 font-medium text-slate-500 text-right">结算金额</th>
            <th className="p-4 font-medium text-slate-500">结算状态</th>
            <th className="p-4 font-medium text-slate-500">开票状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      case 'invoice':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">发票号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">发票类型</th>
            <th className="p-4 font-medium text-slate-500 text-right">金额</th>
            <th className="p-4 font-medium text-slate-500">开票日期</th>
            <th className="p-4 font-medium text-slate-500">状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      case 'payment':
        return (
          <tr>
            {selectHeader}
            <th className="p-4 font-medium text-slate-500">付款单号</th>
            <th className="p-4 font-medium text-slate-500">供应商</th>
            <th className="p-4 font-medium text-slate-500">付款方式</th>
            <th className="p-4 font-medium text-slate-500 text-right">付款金额</th>
            <th className="p-4 font-medium text-slate-500">付款时间</th>
            <th className="p-4 font-medium text-slate-500">状态</th>
            <th className="p-4 font-medium text-slate-500">操作</th>
          </tr>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">单据管理</h2>
        <p className="text-sm text-slate-500 mt-1">统一管理各类业务单据</p>
      </div>

      {/* Tab 切换 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-2">
          {Object.values(DOC_TYPES).map(docType => {
            const count = allDocuments[docType.key]?.length || 0;
            const Icon = docType.icon;
            const isActive = activeDocType === docType.key;

            return (
              <button
                key={docType.key}
                onClick={() => handleTypeChange(docType.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon size={16} />
                {docType.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-white/20' : 'bg-slate-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 筛选条件 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* 搜索框 */}
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="搜索单据号、订单号、送货单号、供应商、车牌号..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* 基地筛选 */}
          <select
            value={filterBase}
            onChange={(e) => setFilterBase(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">全部基地</option>
            {bases.map(base => (
              <option key={base} value={base}>{base}</option>
            ))}
          </select>

          {/* 供应商筛选 */}
          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">全部供应商</option>
            {suppliers.map(supplier => (
              <option key={supplier} value={supplier}>{supplier}</option>
            ))}
          </select>

          {/* 状态筛选 */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">全部状态</option>
            {docConfig.statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {/* 预约时间筛选（预到货通知单、收货单、入库单显示） */}
          {(activeDocType === 'arrival_notice' || activeDocType === 'receipt' || activeDocType === 'stockin') && (
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="开始日期"
              />
              <span className="text-slate-400">至</span>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="结束日期"
              />
              {(filterDateStart || filterDateEnd) && (
                <button
                  onClick={() => {
                    setFilterDateStart('');
                    setFilterDateEnd('');
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {/* 清除筛选 */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
            >
              <X size={14} />
              清除筛选
            </button>
          )}

          {/* 批量结算按钮 - 仅入库单显示 */}
          {activeDocType === 'stockin' && unsettledStockins.length > 0 && (
            <button
              onClick={openBatchSettlement}
              className="flex items-center gap-1 px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
            >
              <CheckSquare size={16} />
              批量结算
              <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                {unsettledStockins.length}
              </span>
            </button>
          )}

          {/* 批量开票按钮 - 仅结算单显示 */}
          {activeDocType === 'settlement' && pendingInvoiceSettlements.length > 0 && (
            <button
              onClick={openBatchInvoice}
              className="flex items-center gap-1 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
            >
              <CheckSquare size={16} />
              批量开票
              <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                {pendingInvoiceSettlements.length}
              </span>
            </button>
          )}
        </div>

        {/* 筛选结果提示 */}
        {hasFilters && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Filter size={14} />
            <span>找到 {filteredDocs.length} 条记录</span>
          </div>
        )}
      </div>

      {/* 单据列表 */}
      {filteredDocs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={24} className="text-slate-400" />
          </div>
          <p className="text-slate-500">暂无{DOC_TYPES[activeDocType].label}</p>
          {hasFilters && (
            <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
              {renderTableHeader()}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDocs.map(doc => {
                const isSelected = selectedIds.includes(doc.id);
                // 使用 isSelectable 函数或 selectableStatus 判断是否可选
                const isSelectable = docConfig.canSelect && (
                  docConfig.isSelectable
                    ? docConfig.isSelectable(doc)
                    : doc.status === docConfig.selectableStatus
                );
                // 获取下一步操作（用于 tooltip）
                const getTooltip = () => {
                  if (!isSelectable) {
                    if (docConfig.key === 'settlement') {
                      return '仅"已结算"的结算单可选择';
                    }
                    return `仅状态为"${docConfig.selectableStatus}"的单据可选择`;
                  }
                  return '';
                };

                return (
                  <tr
                    key={doc.id}
                    className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => onDocumentClick?.(doc)}
                  >
                    {/* 复选框列 */}
                    {docConfig.canSelect && (
                      <td className="p-4 w-12" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSelect(doc.id)}
                          disabled={!isSelectable}
                          className={`${isSelectable ? 'text-slate-400 hover:text-slate-600' : 'text-slate-200 cursor-not-allowed'}`}
                          title={getTooltip()}
                        >
                          {isSelected ? (
                            <CheckSquare size={18} className="text-blue-500" />
                          ) : (
                            <Square size={18} />
                          )}
                        </button>
                      </td>
                    )}
                    {renderTableColumns(doc)}
                    <td className="p-4">
                      <button
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDocumentClick?.(doc);
                        }}
                      >
                        <Eye size={14} />
                        查看详情
                        <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 底部操作栏 */}
      {docConfig.canSelect && selectedIds.length > 0 && (() => {
        // 获取下一步操作信息
        const getNextActionInfo = () => {
          if (docConfig.getNextAction) {
            // 对于结算单，检查选中单据的开票状态
            const allNotInvoiced = selectedDocs.every(d => d.invoiceStatus === '未开票');
            const allInvoiced = selectedDocs.every(d => d.invoiceStatus === '已开票');
            if (allNotInvoiced) {
              return { nextType: 'invoice', nextLabel: '发票单' };
            }
            if (allInvoiced) {
              return { nextType: 'payment', nextLabel: '付款单' };
            }
            // 混合状态
            return { nextType: null, nextLabel: null, mixed: true };
          }
          return { nextType: docConfig.nextType, nextLabel: docConfig.nextLabel };
        };
        const actionInfo = getNextActionInfo();

        return (
          <div className="fixed bottom-6 left-72 right-8 z-40">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-600">
                  已选择 <span className="font-bold text-blue-600">{selectedDocs.length}</span> 条单据
                </span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  <XCircle size={14} />
                  清除选择
                </button>
                {/* 结算单显示开票状态统计 */}
                {activeDocType === 'settlement' && (
                  <span className="text-xs text-slate-400">
                    (未开票: {selectedDocs.filter(d => d.invoiceStatus === '未开票').length}，
                    已开票: {selectedDocs.filter(d => d.invoiceStatus === '已开票').length})
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">
                  合计金额: <span className="font-bold text-red-600">
                    ¥{selectedDocs.reduce((sum, doc) => sum + (doc.amount || 0), 0).toLocaleString()}
                  </span>
                </span>
                {actionInfo.mixed ? (
                  <span className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    请选择相同开票状态的单据
                  </span>
                ) : (
                  <button
                    onClick={handleGenerate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <ArrowRight size={16} />
                    生成{actionInfo.nextLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 生成单据弹窗 */}
      <GenerateModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onConfirm={confirmGenerate}
        selectedDocs={selectedDocs}
        targetType={docConfig?.getNextAction ? docConfig.getNextAction(selectedDocs[0] || {})?.nextType : docConfig?.nextType}
        targetLabel={docConfig?.getNextAction ? docConfig.getNextAction(selectedDocs[0] || {})?.nextLabel : docConfig?.nextLabel}
      />

      {/* 批量结算弹窗 */}
      <BatchSettlementModal
        isOpen={showBatchSettlementModal}
        onClose={() => {
          setShowBatchSettlementModal(false);
          setBatchSelectedIds([]);
        }}
        onConfirm={confirmBatchSettlement}
        stockins={unsettledStockins}
        onToggleSelect={toggleBatchSelect}
        selectedIds={batchSelectedIds}
      />

      {/* 批量开票弹窗 */}
      <BatchInvoiceModal
        isOpen={showBatchInvoiceModal}
        onClose={() => {
          setShowBatchInvoiceModal(false);
          setBatchInvoiceSelectedIds([]);
        }}
        onConfirm={confirmBatchInvoice}
        settlements={pendingInvoiceSettlements}
        onToggleSelect={toggleBatchInvoiceSelect}
        selectedIds={batchInvoiceSelectedIds}
      />

      {/* 关联信息提示 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">单据关联说明</p>
        <div className="text-blue-600 space-y-1">
          <p>• 入库单 → 结算单：仅"未发起"的入库单可发起结算</p>
          <p>• 结算单 → 发票单：仅"已确认"的结算单可发起开票</p>
          <p>• 发票单 → 付款单：仅"已开票"的发票单可发起付款申请</p>
        </div>
      </div>
    </div>
  );
}
